import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, Menu, BookOpen, LogOut } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookReaderPage = () => {
  const navigate = useNavigate();
  const { logout, user, getAuthHeaders } = useAuth();
  const [bookData, setBookData] = useState(null);
  const [currentView, setCurrentView] = useState("introduction"); // introduction, chapter-1, chapter-2, etc., conclusion
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const response = await axios.get(`${API}/book/content`, {
          headers: getAuthHeaders()
        });
        setBookData(response.data);
      } catch (error) {
        if (error.response?.status === 403) {
          alert("Vous devez acheter le livre pour le lire !");
          navigate("/");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchBook();
  }, [getAuthHeaders, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#F97316]"></div>
      </div>
    );
  }

  if (!bookData) return null;

  const getCurrentContent = () => {
    if (currentView === "introduction") {
      return { title: "Introduction", content: bookData.introduction };
    } else if (currentView === "conclusion") {
      return { title: "Conclusion", content: bookData.conclusion };
    } else {
      const chapterNum = parseInt(currentView.split("-")[1]);
      const chapter = bookData.chapters.find(c => c.chapter_number === chapterNum);
      return { title: chapter?.chapter_title || `Chapitre ${chapterNum}`, content: chapter?.content || "" };
    }
  };

  const goToNext = () => {
    if (currentView === "introduction") {
      setCurrentView("chapter-1");
    } else if (currentView.startsWith("chapter-")) {
      const currentChapter = parseInt(currentView.split("-")[1]);
      if (currentChapter < bookData.chapters.length) {
        setCurrentView(`chapter-${currentChapter + 1}`);
      } else {
        setCurrentView("conclusion");
      }
    }
  };

  const goToPrev = () => {
    if (currentView === "conclusion") {
      setCurrentView(`chapter-${bookData.chapters.length}`);
    } else if (currentView.startsWith("chapter-")) {
      const currentChapter = parseInt(currentView.split("-")[1]);
      if (currentChapter > 1) {
        setCurrentView(`chapter-${currentChapter - 1}`);
      } else {
        setCurrentView("introduction");
      }
    }
  };

  const canGoNext = () => {
    if (currentView === "conclusion") return false;
    if (currentView === "introduction") return true;
    const currentChapter = parseInt(currentView.split("-")[1]);
    return currentChapter < bookData.chapters.length;
  };

  const canGoPrev = () => {
    return currentView !== "introduction";
  };

  const currentContent = getCurrentContent();

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-[#1E3A8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {bookData.title}
                </h1>
                <p className="text-xs text-gray-500">{bookData.author}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMenu(!showMenu)}
              >
                <Menu className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-[#475569] hover:text-[#F97316]"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Table of Contents Sidebar */}
      {showMenu && (
        <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowMenu(false)}>
          <div className="fixed left-0 top-0 h-full w-80 bg-white shadow-2xl p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-6 text-[#1E3A8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Table des matières</h2>
            
            <div className="space-y-2">
              <button
                onClick={() => { setCurrentView("introduction"); setShowMenu(false); }}
                className={`w-full text-left px-4 py-3 rounded-lg transition ${currentView === "introduction" ? "bg-orange-100 text-orange-600 font-semibold" : "hover:bg-gray-100 text-gray-700"}`}
              >
                Introduction
              </button>

              {bookData.chapters.map((chapter) => (
                <button
                  key={chapter.chapter_number}
                  onClick={() => { setCurrentView(`chapter-${chapter.chapter_number}`); setShowMenu(false); }}
                  className={`w-full text-left px-4 py-3 rounded-lg transition ${currentView === `chapter-${chapter.chapter_number}` ? "bg-orange-100 text-orange-600 font-semibold" : "hover:bg-gray-100 text-gray-700"}`}
                >
                  {chapter.chapter_title}
                </button>
              ))}

              <button
                onClick={() => { setCurrentView("conclusion"); setShowMenu(false); }}
                className={`w-full text-left px-4 py-3 rounded-lg transition ${currentView === "conclusion" ? "bg-orange-100 text-orange-600 font-semibold" : "hover:bg-gray-100 text-gray-700"}`}
              >
                Conclusion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Reading Area */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <Card className="shadow-xl">
          <CardContent className="p-8 md:p-12">
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1E3A8A] mb-8"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {currentContent.title}
            </h2>

            <div 
              className="prose prose-lg max-w-none text-[#475569] leading-relaxed"
              style={{ whiteSpace: 'pre-wrap' }}
            >
              {currentContent.content}
            </div>
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center mt-8">
          <Button
            onClick={goToPrev}
            disabled={!canGoPrev()}
            variant="outline"
            className="gap-2"
          >
            <ChevronLeft className="w-5 h-5" />
            Précédent
          </Button>

          <span className="text-sm text-gray-500">
            {user?.phone}
          </span>

          <Button
            onClick={goToNext}
            disabled={!canGoNext()}
            className="gap-2 bg-[#F97316] hover:bg-[#EA580C]"
          >
            Suivant
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default BookReaderPage;
